import { Agent } from 'ag-common-lib/public-api';
import {
  PlatformAgentDerivedKeys,
  PlatformAgentResolvedNameKeys,
} from 'ag-common-lib/lib/contracts/reporting-api/response/platform-agents';

/**
 * Keys the projection adds that are not fields of the agent document: two names resolved
 * by joining agency and MGA, and one boolean derived from the token map.
 */
const PROJECTION_ONLY_KEYS: string[] = [
  PlatformAgentResolvedNameKeys.agencyName,
  PlatformAgentResolvedNameKeys.mgaName,
  PlatformAgentDerivedKeys.hasFcmTokens,
];

/**
 * Drops the projection-only keys from an agent before it is written back to Firestore.
 *
 * Only the two screens that write a WHOLE agent object need this — the prospect editor
 * and the merge tool. Everything else writes named fields, so it cannot carry a derived
 * key across by accident. Without it, reading an agent from SQL and saving it would
 * persist `agency_name`, `mga_name` and `hasFcmTokens` onto the document as real fields,
 * where they would then be stale forever: nothing updates them, and the next agency
 * rename would leave the copy behind.
 *
 * Deliberately NOT done inside the search service: the grids render the resolved names,
 * so stripping them on the way in would break the columns that exist to show them.
 */
export function stripProjectionOnlyFields<T extends Partial<Agent>>(agent: T): T {
  if (!agent) {
    return agent;
  }

  const clean = { ...agent };
  PROJECTION_ONLY_KEYS.forEach(key => {
    delete clean[key];
  });

  return clean;
}
